import React, { useEffect } from "react";
import { Box, Stack, IconButton, Tooltip, Select, MenuItem, InputLabel, FormControl } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatClearIcon from "@mui/icons-material/FormatClear";

import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import { Extension } from "@tiptap/core";

// Minimal FontSize extension using TextStyle mark
const FontSize = Extension.create({
    name: "fontSize",
    addGlobalAttributes() {
        return [
            {
                types: ["textStyle"],
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: element => element.style.fontSize || null,
                        renderHTML: attributes => {
                            if (!attributes.fontSize) return {};
                            return { style: `font-size: ${attributes.fontSize}` };
                        },
                    },
                },
            },
        ];
    },
});

/**
 * Reusable rich-text editor (HTML) powered by Tiptap.
 * Props:
 *  - value: string (HTML)
 *  - onChange: (html: string) => void
 *  - placeholder?: string
 *  - minHeight?: number (px)
 *  - fonts?: string[] (CSS font-family names)
 *  - sizes?: string[] (values like '12px','14px','1rem')
 */
export default function RichTextEditor({
    value,
    onChange,
    placeholder = "Escribe aquí...",
    minHeight = 160,
    fonts = ["inherit", "Arial, Helvetica, sans-serif", "'Times New Roman', Times, serif", "'Courier New', Courier, monospace"],
    sizes = ["12px", "14px", "16px", "18px", "24px"],
}) {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TextStyle,
            Color,
            FontFamily,
            FontSize,
        ],
        content: value || "<p></p>",
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
        editorProps: {
            attributes: {
                style: `min-height:${minHeight}px; padding:12px; outline:none;`,
                class: "tiptap-content",
            },
        },
    });

    useEffect(() => {
        // Load external changes (e.g., when opening edit dialog)
        if (!editor) return;
        const current = editor.getHTML();
        if ((value || "") !== current) {
            editor.commands.setContent(value || "<p></p>", true);
        }
    }, [value, editor]);

    if (!editor) return null;

    const isActive = (name, attrs) => editor.isActive(name, attrs);

    return (
        <Box>
            {/* Toolbar */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }}>
                <Tooltip title="Negrita"><span><IconButton size="small" color={isActive('bold') ? 'primary' : 'default'} onClick={() => editor.chain().focus().toggleBold().run()}><FormatBoldIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Cursiva"><span><IconButton size="small" color={isActive('italic') ? 'primary' : 'default'} onClick={() => editor.chain().focus().toggleItalic().run()}><FormatItalicIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Subrayado"><span><IconButton size="small" color={isActive('underline') ? 'primary' : 'default'} onClick={() => editor.chain().focus().toggleUnderline().run()}><FormatUnderlinedIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Tachado"><span><IconButton size="small" color={isActive('strike') ? 'primary' : 'default'} onClick={() => editor.chain().focus().toggleStrike().run()}><StrikethroughSIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Viñetas"><span><IconButton size="small" color={isActive('bulletList') ? 'primary' : 'default'} onClick={() => editor.chain().focus().toggleBulletList().run()}><FormatListBulletedIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Numeración"><span><IconButton size="small" color={isActive('orderedList') ? 'primary' : 'default'} onClick={() => editor.chain().focus().toggleOrderedList().run()}><FormatListNumberedIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Limpiar formato"><span><IconButton size="small" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><FormatClearIcon fontSize="small" /></IconButton></span></Tooltip>

                <FormControl size="small" sx={{ minWidth: 170, ml: 1 }}>
                    <InputLabel id="font-family-label">Fuente</InputLabel>
                    <Select
                        labelId="font-family-label"
                        label="Fuente"
                        value={editor.getAttributes('textStyle')?.fontFamily || "inherit"}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (v === "inherit") editor.commands.unsetFontFamily();
                            else editor.commands.setFontFamily(v);
                        }}
                    >
                        {fonts.map(f => (
                            <MenuItem key={f} value={f}>
                                <span style={{ fontFamily: f }}>{f}</span>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel id="font-size-label">Tamaño</InputLabel>
                    <Select
                        labelId="font-size-label"
                        label="Tamaño"
                        value={editor.getAttributes('textStyle')?.fontSize || "16px"}
                        onChange={(e) => {
                            const v = e.target.value;
                            editor.chain().focus().setMark('textStyle', { fontSize: v }).run();
                        }}
                    >
                        {sizes.map(s => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <input
                    type="color"
                    value={editor.getAttributes('textStyle')?.color || '#000000'}
                    onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                    title="Color de texto"
                    style={{ width: 36, height: 32, border: '1px solid #ccc', padding: 0, background: 'transparent', cursor: 'pointer' }}
                />
            </Stack>

            {/* Editable area */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <EditorContent editor={editor} />
            </Box>

            {/* Placeholder CSS */}
            <style>{`
        .tiptap-content p.is-editor-empty:first-child::before {
          content: '${placeholder.replace(/'/g, "\\'")}';
          color: #9e9e9e;
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
        </Box>
    );
}
